import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;
const BASE = `${API_URL}/admin/company-classification`;

// --------------------------------------------------------------------------- //
// Classification des entreprises (overrides, industries) + paliers A-player.
// Les règles sont évaluées côté back/ ; chaque écriture déclenche le reclassement
// des entreprises concernées puis le recalcul des candidats (sous ~1 min).
// --------------------------------------------------------------------------- //

export interface CompanyTypeOption {
  value: string;
  label: string;
}

export interface CompanyTypeOverride {
  id: number;
  company_type: string;
  name: string | null;
  aliases: string[];
  linkedin_company_ids: number[];
  note: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OverridePayload {
  company_type: string;
  name: string | null;
  aliases: string[];
  linkedin_company_ids: number[];
  note: string | null;
}

export interface IndustryType {
  linkedin_id: number;
  name: string | null;
  parent_linkedin_id: number | null;
  parent_name: string | null;
  default_type: string | null;
  override_type: string | null;
  override_untyped: boolean;
  own_type: string | null;
  effective_type: string | null;
  inherited_from: number | null;
  inherited_from_name: string | null;
  note: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface IndustryUpdatePayload {
  override_type: string | null;
  override_untyped: boolean;
  note?: string | null;
}

export interface ClassifiedCompany {
  id: number;
  name: string | null;
  external_ref: string | null;
  linkedin_company_id: number | null;
  company_type: string | null;
  company_type_source: string | null;
  industries: { linkedin_id: number | null; name: string | null }[];
  override_id: number | null;
}

export interface ClassifiedCompaniesResponse {
  items: ClassifiedCompany[];
  total: number;
  page: number;
  size: number;
}

export interface CareerTiers {
  progressing_min: number;
  strong_min: number;
  distribution: { bucket: string; count: number }[];
  counts: { not_evaluable: number; below: number; progressing: number; strong: number };
}

async function errorMessage(res: Response | undefined | null, fallback: string): Promise<string> {
  const body = await res?.json().catch(() => null);
  const detail = body?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return String(detail[0].msg);
  return fallback;
}

function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

async function getJson<T>(url: string, navigate: NavigateFunction, fallback: string): Promise<T> {
  const res = await apiFetch(url, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error(await errorMessage(res, fallback));
  return res.json();
}

async function sendJson<T>(
  url: string,
  method: "POST" | "PUT",
  payload: unknown,
  navigate: NavigateFunction,
  fallback: string,
): Promise<T> {
  const res = await apiFetch(
    url,
    { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) },
    navigate,
  );
  if (!res?.ok) throw new Error(await errorMessage(res, fallback));
  return res.json();
}

export function getCompanyTypes(navigate: NavigateFunction): Promise<CompanyTypeOption[]> {
  return getJson(`${BASE}/types`, navigate, "Erreur lors du chargement des types");
}

export function listOverrides(
  navigate: NavigateFunction,
  filters: { q?: string; type?: string } = {},
): Promise<CompanyTypeOverride[]> {
  return getJson(`${BASE}/overrides${query(filters)}`, navigate, "Erreur lors du chargement des corrections");
}

export function createOverride(payload: OverridePayload, navigate: NavigateFunction): Promise<CompanyTypeOverride> {
  return sendJson(`${BASE}/overrides`, "POST", payload, navigate, "Échec de la création");
}

export function updateOverride(
  id: number,
  payload: OverridePayload,
  navigate: NavigateFunction,
): Promise<CompanyTypeOverride> {
  return sendJson(`${BASE}/overrides/${id}`, "PUT", payload, navigate, "Échec de la mise à jour");
}

export async function deleteOverride(id: number, navigate: NavigateFunction): Promise<void> {
  const res = await apiFetch(`${BASE}/overrides/${id}`, { method: "DELETE" }, navigate);
  if (!res?.ok) throw new Error(await errorMessage(res, "Échec de la suppression"));
}

export function listIndustries(
  navigate: NavigateFunction,
  filters: { q?: string; type?: string; only_overridden?: boolean } = {},
): Promise<IndustryType[]> {
  return getJson(`${BASE}/industries${query(filters)}`, navigate, "Erreur lors du chargement des industries");
}

export function updateIndustry(
  linkedinId: number,
  payload: IndustryUpdatePayload,
  navigate: NavigateFunction,
): Promise<IndustryType> {
  return sendJson(`${BASE}/industries/${linkedinId}`, "PUT", payload, navigate, "Échec de la mise à jour");
}

export function listClassifiedCompanies(
  navigate: NavigateFunction,
  filters: { q?: string; type?: string; source?: string; page?: number; size?: number } = {},
): Promise<ClassifiedCompaniesResponse> {
  return getJson(`${BASE}/companies${query(filters)}`, navigate, "Erreur lors du chargement des entreprises");
}

export function reclassifyCompany(id: number, navigate: NavigateFunction): Promise<{ status: string }> {
  return sendJson(`${BASE}/companies/${id}/reclassify`, "POST", {}, navigate, "Échec du reclassement");
}

export function getCareerTiers(navigate: NavigateFunction): Promise<CareerTiers> {
  return getJson(`${BASE}/tiers`, navigate, "Erreur lors du chargement des paliers");
}

export function updateCareerTiers(
  payload: { progressing_min: number; strong_min: number },
  navigate: NavigateFunction,
): Promise<CareerTiers> {
  return sendJson(`${BASE}/tiers`, "PUT", payload, navigate, "Échec de l'enregistrement des paliers");
}
